import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useMall } from "@/context/MallContext";
import { useShopShown } from "@/context/SiteModeContext";
import { mediaUrl } from "@/lib/api";
import { ruleFor } from "@/siteMap";
import { TOP_MENU } from "@/lib/nav";
import { useKeepStore } from "@/components/layout/Crumbs";

const HOUSE = "the-sign-shop";

/** The site index in three drawers: what you can order, how to rent this site, and our custom web work. */
const GROUPS = (phoneRaw, policies) => [
    {
        key: "shop", label: "Order", note: "Everything a customer can order",
        links: [
            ...TOP_MENU.map((n) => ({ key: n.key, to: n.to, label: n.label })),
            { key: "dtf", to: "/shirts/dtf/gang-sheet", label: "DTF Gang Sheet Builder" },
            { key: "wholesale", to: "/wholesale", label: "Wholesale & coupons" },
            { key: "track", to: "/track", label: "Track an order" },
            { key: "pay", to: "/pay", label: "Pay a balance" },
            { key: "policies", to: policies, label: "Shop policies" },
        ],
    },
    {
        key: "own", label: "Own a print shop?", note: "Rent this website with your name on it",
        blurb: "13thav rents out white-label print shop websites. You get a site just like this one — same live designers — with your name, logo, colors and web address on every page. Your money goes to your own Stripe account; print what you want yourself and 13thav prints the rest.",
        links: [
            { key: "whitelabel", to: "/whitelabel", label: "Get this website for your shop →", strong: true },
            { key: "signup", to: "/signup/shop", label: "Start your shop" },
            { key: "sell", to: "/supplies/sell", label: "Post a classified ad" },
        ],
    },
    {
        key: "web", label: "Website design", note: "Custom sites for any business, live in 7 days",
        blurb: "We also build fully custom websites for any business, not just print shops. Built with AI, live in 7 days. Try the demo playground before you buy.",
        links: [
            { key: "web", to: "/website-design", label: "Get a quote →", strong: true },
            { key: "playground", to: "/website-design#playground", label: "Try the demo playground" },
            { key: "text", href: `sms:${phoneRaw}?body=I%20want%20a%20new%20website.`, label: "Text us about a site" },
        ],
    },
];

function Drawer({ group, keep }) {
    return (
        <div data-testid={`footer-group-${group.key}`}>
            <p className="font-display text-sm font-black uppercase tracking-[0.18em] text-white">{group.label}</p>
            <p className="mt-0.5 text-xs text-zinc-500">{group.note}</p>
            {group.blurb && <p className="mt-3 text-xs leading-relaxed text-zinc-400" data-testid={`footer-blurb-${group.key}`}>{group.blurb}</p>}
            <ul className="mt-3 space-y-2 text-sm text-zinc-300">
                {group.links.map((l) => (
                    <li key={l.key}>
                        {l.href
                            ? <a href={l.href} className="hover:text-white">{l.label}</a>
                            : <Link to={keep(l.to)} data-testid={`footer-nav-${l.key}`} className={l.strong ? "font-semibold text-white hover:underline" : "hover:text-white"}>{l.label}</Link>}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Phase 10 — the original full footer, back: the shop's block, the site index, the powered-by line. */
export default function ShopFooter() {
    const settings = useSettings();
    const { shown: store, floor, isHouse, solo } = useMall();
    const show = useShopShown();
    const keep = useKeepStore();
    const b = settings?.business || {};
    const name = store?.name || b.short_name || "13thav";
    const address = store ? store.pickup_address : b.address;
    const phone = store ? store.phone : b.phone;
    const phoneRaw = (phone || b.phone_raw || "").replace(/[^\d+]/g, "") || "+13475733875";
    const locker = store ? store.pickup_mode === "locker" : true;
    const hours = (store ? store.hours : b.hours) || "";
    const policies = `/stores/${store?.slug || floor?.home_store || HOUSE}/policies`;
    const house = isHouse && !solo;   // a tenant's site keeps only the one discreet powered-by line
    const onMap = (l) => !l.to || show(ruleFor(l.to.split(/[?#]/)[0]));
    const groups = GROUPS(phoneRaw, policies).filter((g) => house || g.key !== "own")
        .map((g) => ({ ...g, links: g.links.filter(onMap) })).filter((g) => g.links.length);

    return (
        <footer className="dark-block relative border-t-4 border-zinc-900 bg-[#0b0b0b] pt-14 text-white" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className={`grid gap-10 ${groups.length >= 3 ? "md:grid-cols-[1.3fr_1fr_1fr_1fr]" : "md:grid-cols-[1.3fr_1fr_1fr]"}`}>
                    <div data-testid="footer-shop-block">
                        <Link to={store && store.slug !== floor?.home_store ? `/stores/${store.slug}` : "/"} className="inline-flex items-center gap-3" data-testid="footer-logo">
                            {store?.logo
                                ? <img src={mediaUrl(store.logo)} alt="" className="h-10 w-10 bg-white object-contain p-0.5" />
                                : <span className="flex h-10 w-10 items-center justify-center bg-white font-display text-base font-black text-black">{name.slice(0, 1)}</span>}
                            <span className="font-display text-2xl font-black uppercase tracking-tight text-white" data-testid="footer-shop-name">{name}</span>
                        </Link>
                        {store?.tagline && <p className="mt-3 max-w-xs text-sm text-zinc-400">{store.tagline}</p>}
                        <ul className="mt-5 space-y-3 text-sm text-zinc-300">
                            {address && <li className="flex items-start gap-2" data-testid="footer-address"><MapPin size={16} className="mt-0.5 shrink-0 text-white" /> {address}</li>}
                            {phone && <li><a href={`tel:${phoneRaw}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone size={16} className="text-white" /> {phone}</a></li>}
                            {hours && <li className="font-mono text-xs uppercase tracking-widest text-zinc-500" data-testid="footer-hours">{hours}</li>}
                            {locker && <li className="font-mono text-xs uppercase tracking-widest text-zinc-500" data-testid="footer-locker">24-hour locker pickup · No walk-ins</li>}
                            {!store && b.socials?.instagram && <li><a href={b.socials.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white"><Instagram size={16} className="text-white" /> @13thav</a></li>}
                        </ul>
                    </div>
                    {groups.map((g) => <Drawer key={g.key} group={g} keep={keep} />)}
                </div>
            </div>
            <div className="mt-12 overflow-hidden">
                <div className="select-none text-center font-display text-[17vw] font-black uppercase leading-[0.8] tracking-tighter text-white/[0.05]">{name}</div>
            </div>
            <div className="border-t border-white/10 py-6">
                <p className="text-center text-xs text-zinc-500" data-testid="footer-powered-line">
                    © {new Date().getFullYear()} {name}{store?.city ? ` · ${store.city}` : " · Brooklyn, NY"} ·{" "}
                    <Link to="/whitelabel" className="font-semibold text-zinc-300 underline hover:text-white" data-testid="footer-powered-by">
                        {house ? "Own a print shop? Get this website for your shop" : "Powered by 13thav — get a site like this"}
                    </Link>
                </p>
            </div>
        </footer>
    );
}
